import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getOcRequests } from '../../clients/oc_request_client';
import OcRequestStatusBadge from '../../components/OrganoidRequest/OcRequestStatusBadge';
import OcWorkspaceShell from '../../components/OrganoidRequest/OcWorkspaceShell';
import {
  OC_BUCKET_OPTIONS,
  getOcStageLabel,
} from '../../utils/ocRequest';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcRequestsPage';

/**
 * Internal OC request inbox at /organoid-requests.
 */
const OcRequestsPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState('');
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);

  const load = useCallback(
    async (opts = {}) => {
      if (!token) return;
      setLoading(true);
      const result = await getOcRequests(token, {
        search: opts.search ?? search,
        bucket: opts.bucket ?? bucket,
        assignedToMe: opts.assignedToMe ?? assignedToMe,
        pageNum: 1,
      });
      setLoading(false);
      if (!result.success) {
        showToast(result.messages?.[0] || 'Failed to load requests', 'error', 'top-right', 4000);
        return;
      }
      setResults(result.results || []);
      setNextUrl(result.next);
      logInfo(LOG_CONTEXT, 'list loaded', { count: result.results?.length });
    },
    [token, search, bucket, assignedToMe]
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <OcWorkspaceShell wide theme="yellow">
      <div className="oc-page-heading">
        <h2 className="mb-0">Organoid requests</h2>
        {!isViewer ? (
        <Button
          as={Link}
          to="/request/new"
          className="oc-primary-btn ms-auto"
          onClick={() =>
            logInfo(LOG_CONTEXT, 'opening public organoid request form', {
              path: '/request/new',
            })
          }
        >
          New request
        </Button>
        ) : null}
      </div>

      <div className="oc-toolbar">
      <Row className="g-2 align-items-end">
        <Col md={4}>
          <Form.Control
            placeholder="Search number, org, email"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') load({ search: e.target.value });
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Select value={bucket} onChange={e => setBucket(e.target.value)}>
            {OC_BUCKET_OPTIONS.map(opt => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md="auto">
          <Form.Check
            type="checkbox"
            label="Assigned to me"
            checked={assignedToMe}
            onChange={e => setAssignedToMe(e.target.checked)}
          />
        </Col>
        <Col md="auto">
          <Button className="oc-primary-btn" onClick={() => load()}>
            Apply
          </Button>
        </Col>
      </Row>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="oc-table-wrap">
        <Table striped bordered hover responsive className="mb-0">
          <thead>
            <tr>
              <th>Number</th>
              <th>Status</th>
              <th>Stage</th>
              <th>Organization</th>
              <th>Contact</th>
              <th>Assignee</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {!results.length ? (
              <tr>
                <td colSpan={7}>No requests found.</td>
              </tr>
            ) : (
              results.map(row => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/organoid-requests/${row.id}`}>{row.request_number}</Link>
                  </td>
                  <td>
                    <OcRequestStatusBadge status={row.status} />
                  </td>
                  <td>{getOcStageLabel(row.current_stage)}</td>
                  <td>{row.organization}</td>
                  <td>
                    {row.contact_person}
                    <div className="small text-muted">{row.contact_email}</div>
                  </td>
                  <td>{row.assigned_to_username || '—'}</td>
                  <td>
                    {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>
      )}
      {nextUrl ? (
        <p className="text-muted small mt-2">More pages available via API pagination.</p>
      ) : null}
    </OcWorkspaceShell>
  );
};

export default OcRequestsPage;
